import React, { Component, ErrorInfo, ReactNode } from 'react';

interface Props {
  children: ReactNode;
  showSidebar?: boolean;
}

interface State {
  hasError: boolean;
  error?: Error;
  errorInfo?: ErrorInfo;
}

class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    this.setState({ errorInfo });
    console.error('Uncaught error:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      const isDevelopment =
        import.meta.env.DEV ||
        window.location.hostname === 'localhost';

      return (
        <div className="flex items-center justify-center min-h-[50vh] p-8">
          <div className="max-w-md w-full text-center space-y-4">
            <div className="w-16 h-16 mx-auto flex items-center justify-center bg-error/10 border border-error/20">
              <span className="material-icons text-error text-3xl">error_outline</span>
            </div>
            <h2 className="text-xl font-display font-bold text-on-surface">
              Something went wrong
            </h2>
            <p className="text-sm text-on-surface-variant/60">
              An unexpected error occurred. Please try refreshing the page.
            </p>
            <button
              onClick={() => window.location.reload()}
              className="btn btn-primary btn-sm gap-2"
            >
              <span className="material-icons text-sm">refresh</span>
              Refresh Page
            </button>
            {isDevelopment && (
              <details className="mt-6 text-left">
                <summary className="text-xs text-on-surface-variant/40 cursor-pointer font-mono">
                  Error Details (dev only)
                </summary>
                <pre className="mt-2 p-4 bg-surface-container-low border border-outline-variant/20 text-xs text-error/80 overflow-auto max-h-64 whitespace-pre-wrap font-mono">
                  {this.state.error && this.state.error.toString()}
                  {'\n'}
                  {this.state.errorInfo && this.state.errorInfo.componentStack}
                </pre>
              </details>
            )}
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;